import apiEndPOints from "@/lib/services/api.endpoints";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Loader2 } from "lucide-react";
import { RootState, useAppSelector } from "@/lib/redux/root";

export interface LocationItem {
	location: string;
	send_file: string;
}

export interface Region {
	region: string;
	locations: LocationItem[];
}

export interface Country {
	country: string;
	regions: Region[];
}

const HERO_BG_IMAGE =
	"https://www.e-arc.com/wp-content/uploads/2025/10/locationListing-4.jpg";

const LocationsPage = () => {
	const { product: productParam } = useParams();
	const [countries, setCountries] = useState<Country[]>([]);
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(true);

	const { isLocationNotFound } = useAppSelector(
		(state: RootState) => state.location
	);

	const resolveProduct = () => {
		// from URL param
		if (productParam === "arc" || productParam === "riot") {
			return productParam;
		}

		// from domain
		const host = window.location.hostname.toLowerCase();

		if (host.includes("riot")) return "riot";
		if (host.includes("arc")) return "arc";

		// default fallback
		return "arc";
	};

	const product = resolveProduct();

	useEffect(() => {
		const fetchLocations = async () => {
			try {
				const endpoint =
					product === "arc"
						? import.meta.env.VITE_LOCATIONS_API_URL
						: import.meta.env.VITE_LOCATIONS_API_RIOT_URL;

				const res = await fetch(endpoint, {
					method: "GET",
					headers: {
						Accept: "application/json",
						"Content-Type": "application/json",
					},
				});

				const json = await res.json();
				setCountries(json.data || []);
			} catch (e) {
				console.log(e);
			} finally {
				setLoading(false);
			}
		};

		fetchLocations();
	}, [product]);

	const filteredData = useMemo(() => {
		if (!search) return countries;

		return countries
			.map((country) => ({
				...country,
				regions: country.regions
					.map((region) => ({
						...region,
						locations: region.locations.filter((loc) =>
							loc.location.toLowerCase().includes(search.toLowerCase())
						),
					}))
					.filter((region) => region.locations.length > 0),
			}))
			.filter((country) => country.regions.length > 0);
	}, [countries, search]);

	// Sort countries to ensure United States appears first
	const sortedData = useMemo(() => {
		if (!filteredData) return [];

		return [...filteredData].sort((a, b) => {
			if (
				a.country.toLowerCase().includes("united states") ||
				a.country.toLowerCase() === "us"
			)
				return -1;
			if (
				b.country.toLowerCase().includes("united states") ||
				b.country.toLowerCase() === "us"
			)
				return 1;
			return a.country.localeCompare(b.country);
		});
	}, [filteredData]);

	if (loading) {
		return (
			<div className='flex min-h-[90vh] flex-col items-center justify-center gap-3'>
				<Loader2 className='h-10 w-10 animate-spin text-red-600' />
				<p className='text-gray-600'>Fetching locations…</p>
			</div>
		);
	}

	return (
		<div className='w-full bg-white'>
			{/* Hero Section */}
			<div
				className='relative flex h-[400px] items-center justify-center bg-cover bg-center transition-all duration-700'
				style={{ backgroundImage: `url(${HERO_BG_IMAGE})` }}
			>
				<div className='absolute inset-0 bg-black/50 backdrop-blur-[2px]' />

				<div className='relative z-10 w-full max-w-4xl px-5 text-center'>
					{/* Dynamic Heading based on State */}
					<h1 className='mb-3 text-5xl font-bold text-white drop-shadow-lg transition-all duration-300'>
						{isLocationNotFound ? (
							<span className='text-red-400'>Location Not Found</span>
						) : (
							"Welcome To Dropfiles"
						)}
					</h1>

					{/* Supportive Text: Changes to provide guidance when lost */}
					<p className='mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-gray-100'>
						{isLocationNotFound ? (
							<>
								We couldn't find a match. Please check your spelling or choose a
								service center below.
							</>
						) : (
							"Share Your Files Securely And Effortlessly. Upload, Share, And Manage Your Documents With Ease."
						)}
					</p>

					{/* Search Bar: Added a conditional "shake" or "glow" if not found */}
					<div
						className={`mx-auto flex max-w-2xl items-center gap-2 rounded-full bg-white p-1.5 shadow-2xl transition-all ${isLocationNotFound ? "ring-2 ring-red-500" : "ring-1 ring-transparent"}`}
					>
						<div className='flex flex-1 items-center px-4'>
							<Search
								className={`mr-3 h-5 w-5 transition-colors ${isLocationNotFound ? "text-red-400" : "text-gray-400"}`}
							/>
							<Input
								type='text'
								placeholder='Search location by City, Country, or Zip'
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className='h-10 border-0 bg-transparent text-base placeholder:text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0'
							/>
						</div>
						<Button
							className='h-11 rounded-full bg-red-600 px-8 font-semibold text-white transition-all hover:bg-red-700 active:scale-95'
							onClick={() => {
								/* Trigger Search Logic */
							}}
						>
							Search
						</Button>
					</div>
				</div>
			</div>

			{/* Results Section */}
			<div className='mx-auto min-h-[500px] max-w-7xl px-5 py-16'>
				{/* UX TIP: We show the full list (countries) even if the search failed. 
               This is the "Safety Net" design pattern.
            */}
				{sortedData.map((country, index) => (
					<div
						key={country.country}
						className={`mb-12 transition-opacity duration-500 ${isLocationNotFound ? "opacity-70" : "opacity-100"}`}
					>
						{/* If we are in "Not Found" mode, we add a helpful sub-header to the list */}
						{isLocationNotFound && index === 0 && (
							<div className='mb-10 rounded-r-lg border-l-4 border-red-500 bg-gray-50 p-4'>
								<p className='font-medium text-gray-700'>
									Showing all available service locations:
								</p>
							</div>
						)}

						<h2 className='mb-8 border-b border-gray-200 pb-5 text-3xl font-bold text-gray-900'>
							{country.country} Locations
						</h2>

						<div className='columns-1 gap-10 md:columns-2 lg:columns-4'>
							{country.regions.map((region) => (
								<div
									key={region.region}
									className='group mb-8 inline-block w-full break-inside-avoid'
								>
									<h4 className='mb-3 cursor-default text-base font-bold tracking-wide text-red-600 uppercase transition-transform group-hover:translate-x-1'>
										{region.region}
									</h4>
									<div className='flex flex-col gap-2'>
										{region.locations.map((loc) => (
											<a
												key={loc.location}
												href={loc.send_file}
												className='flex items-center text-[15px] leading-relaxed text-gray-700 transition-all hover:pl-1 hover:text-red-600'
											>
												<span className='mr-2 h-1.5 w-1.5 rounded-full bg-gray-300 transition-colors group-hover:bg-red-500' />
												{loc.location}
											</a>
										))}
									</div>
								</div>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default LocationsPage;
